/**
 * StatCard — bento dashboard tile.
 *
 * Three visualisation flavours, all driven by REAL data only:
 *   • sparkline — pass ``viz={{type:'sparkline', data:[…]}}``. Renders
 *     a smoothed 10-point line with a soft-fill area beneath.
 *   • ring     — pass ``viz={{type:'ring', value, max}}``. Renders a
 *     progress ring filled to ``value/max``.
 *   • bar      — pass ``viz={{type:'bar', data:[…]}}``. Renders a
 *     mini bar chart, opacity scaled per bar height.
 *
 * Empty-data policy: if the array is empty / max is zero / value is
 * null, the viz simply doesn't render. The card falls back to the
 * headline number alone. We never invent or pad data — a customer
 * with 3 employees should see the literal 3 (or no chart) rather than
 * a phantom upward trend.
 *
 * The legacy ``trend`` prop is preserved so existing call sites that
 * pass `{value, label}` keep working unchanged.
 */
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { TrendingUp, TrendingDown, type LucideIcon } from "lucide-react";

// ════════════════════════════════════════════════════════════════════
// Inline data-viz components
// ════════════════════════════════════════════════════════════════════

/** 10-point sparkline with gradient area fill. Brand-blue line. */
export function Sparkline({
  data,
  color = "#1B18FF",
  width = 100,
  height = 40,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  // Hash the colour into a unique gradient id so multiple sparklines
  // on the same page don't share a fill (and the brand blue + an
  // accent-coloured peer would conflict).
  const gradId = `sl-${color.replace("#", "")}`;
  return (
    <svg width={width} height={height} className="block" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Circular progress ring filled to value/max. */
export function ProgressRing({
  value,
  max = 100,
  size = 56,
  strokeWidth = 5,
  color = "#1B18FF",
}: {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  if (max <= 0) return null;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const pct = Math.min(Math.max(value / max, 0), 1);
  const offset = circumference - pct * circumference;
  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "rotate(-90deg)" }}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgb(var(--color-border))"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </svg>
  );
}

/** Mini bar chart, opacity scaled per bar height. */
export function MiniBar({
  data,
  color = "#1B18FF",
  width = 80,
  height = 32,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data);
  if (max <= 0) {
    // All zeros — flat baseline rather than blank, but still no fake
    // values. Communicates "we measured, the answer is zero".
    return (
      <svg width={width} height={height} aria-hidden="true">
        <rect
          x={0}
          y={height - 1}
          width={width}
          height={1}
          fill={color}
          opacity={0.25}
          rx={0.5}
        />
      </svg>
    );
  }
  const barW = width / data.length - 2;
  return (
    <svg width={width} height={height} className="block" aria-hidden="true">
      {data.map((v, i) => {
        const barH = (v / max) * (height - 2);
        return (
          <rect
            key={i}
            x={i * (barW + 2)}
            y={height - barH}
            width={barW}
            height={barH}
            rx={2}
            fill={color}
            opacity={0.3 + (v / max) * 0.7}
          />
        );
      })}
    </svg>
  );
}

// ════════════════════════════════════════════════════════════════════
// StatCard
// ════════════════════════════════════════════════════════════════════

type VizSpec =
  | { type: "sparkline"; data: number[]; color?: string }
  | { type: "ring"; value: number; max: number; color?: string }
  | { type: "bar"; data: number[]; color?: string };

interface StatCardProps {
  label: string;
  value: string | number;
  /** Optional — falls back when no ``viz`` is supplied. */
  icon?: LucideIcon;
  /** Legacy delta indicator — pass ``{ value: pct, label: ... }``. */
  trend?: { value: number; label: string };
  /** Modern bento viz — sparkline / ring / bar. Renders to the right
   *  of the headline number. Falls back to icon-only when omitted. */
  viz?: VizSpec;
  /** Subtitle line below the headline. Use this for "vs last quarter"
   *  / "no items pending" style context, in place of the legacy
   *  trend prop when you want freeform copy. */
  subtitle?: string;
  /** Tone for the subtitle / trend — affects the small status colour
   *  next to "↑/⚠/—" markers. */
  tone?: "up" | "down" | "neutral";
  className?: string;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  trend,
  viz,
  subtitle,
  tone,
  className,
}: StatCardProps) {
  const toneColor =
    tone === "up"
      ? "text-success"
      : tone === "down"
        ? "text-warning"
        : "text-text-muted";
  const toneGlyph = tone === "up" ? "↑" : tone === "down" ? "⚠" : "·";

  return (
    <Card
      variant="interactive"
      className={cn(
        "flex items-center justify-between gap-4 cursor-default",
        className,
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold uppercase tracking-[0.04em] text-text-muted">
          {label}
        </div>
        <div className="mt-2 text-[32px] font-bold leading-none tabular-nums text-text-primary">
          {value}
        </div>
        {subtitle && (
          <div
            className={cn(
              "mt-2 flex items-center gap-1 text-xs font-medium",
              toneColor,
            )}
          >
            {tone && <span aria-hidden="true">{toneGlyph}</span>}
            <span className="text-text-muted">{subtitle}</span>
          </div>
        )}
        {trend && !subtitle && (
          <div className="mt-2 flex items-center gap-1">
            {trend.value >= 0 ? (
              <TrendingUp
                className="h-3.5 w-3.5 text-success"
                aria-hidden="true"
              />
            ) : (
              <TrendingDown
                className="h-3.5 w-3.5 text-danger"
                aria-hidden="true"
              />
            )}
            <span
              className={cn(
                "text-xs font-medium",
                trend.value >= 0 ? "text-success" : "text-danger",
              )}
            >
              {trend.value > 0 ? "+" : ""}
              {trend.value}%
            </span>
            <span className="text-xs text-text-muted">{trend.label}</span>
          </div>
        )}
      </div>

      {/* Right-side viz — only renders when we have real data to show.
          Falls back to the legacy icon-in-a-tinted-square when no viz
          is provided. */}
      {viz ? (
        <div className="shrink-0 opacity-95">
          {viz.type === "sparkline" && (
            <Sparkline data={viz.data} color={viz.color} />
          )}
          {viz.type === "ring" && (
            <ProgressRing
              value={viz.value}
              max={viz.max}
              color={viz.color}
            />
          )}
          {viz.type === "bar" && (
            <MiniBar data={viz.data} color={viz.color} />
          )}
        </div>
      ) : Icon ? (
        <div className="rounded-bentoSm bg-accent/10 p-2.5 shrink-0">
          <Icon className="h-5 w-5 text-accent" aria-hidden="true" />
        </div>
      ) : null}
    </Card>
  );
}
